// KeyTestButton — small "Test" button that fires the test endpoint for the
// user's ALREADY-SAVED BYOK or YouTube key. Shows a result badge for 5s then
// auto-clears. iter-31-P2c.
//
// Lives on the BYOK card + YouTubeKeyCard so users can re-verify a saved key
// anytime (key could stop working due to quota, revocation, billing issue).

import { useState, useEffect } from "react";
import { Lightning, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function KeyTestButton({ keyType, disabled, testid = "key-test" }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null); // {ok, latency_ms, sample, error}

  const endpoint = keyType === "youtube" ? "/auth/youtube-key/test/saved" : "/auth/byok/test/saved";

  const run = async () => {
    if (disabled || testing) return;
    setTesting(true);
    try {
      const r = await api.post(endpoint, {});
      setResult(r.data);
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || "Could not reach server." });
    } finally { setTesting(false); }
  };

  // Auto-clear the result after 8s so the card returns to clean state.
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), 8000);
    return () => clearTimeout(t);
  }, [result]);

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <button
        type="button"
        onClick={run}
        disabled={disabled || testing}
        data-testid={`${testid}-btn`}
        title={disabled ? "Save a key first, then you can test it anytime." : "Test your saved key with a tiny call right now"}
        className="text-xs px-3 py-1.5 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
      >
        <Lightning size={12} weight="duotone" /> {testing ? "Testing…" : "Test"}
      </button>
      {result && (
        <div
          className={`text-[11px] px-2 py-1 rounded flex items-center gap-1 max-w-[280px] ${result.ok ? "bg-[#EDF4EE] text-[#2F4A34]" : "bg-[#FDF1ED] text-[#8F3E2A]"}`}
          data-testid={`${testid}-result`}
        >
          {result.ok
            ? <CheckCircle size={12} weight="duotone" className="shrink-0" />
            : <XCircle size={12} weight="duotone" className="shrink-0" />}
          <span className="truncate">
            {result.ok ? `Working · ${result.latency_ms}ms` : (result.error || "Test failed.")}
          </span>
        </div>
      )}
    </div>
  );
}
